import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, message, Modal, Form, InputNumber, Input, Descriptions, Popconfirm } from 'antd';
import { RobotOutlined, SearchOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import LlmStatusTag from '../../../shared/components/LlmStatusTag';
import { formatCompactNumber } from '../../../shared/utils/format';

const LlmUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchText, setSearchText] = useState('');

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const [quotaVisible, setQuotaVisible] = useState(false);
  const [quotaSubmitting, setQuotaSubmitting] = useState(false);
  const [quotaTarget, setQuotaTarget] = useState(null);
  const [quotaForm] = Form.useForm();

  const [statusSubmittingId, setStatusSubmittingId] = useState(null);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/admin/llm/users', {
        params: {
          page: pagination.current,
          per_page: pagination.pageSize,
          search: searchText,
        },
      });
      if (response.data?.success) {
        setUsers(response.data.data?.data || response.data.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.data?.total || 0,
        }));
      }
    } catch (error) {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchUsers();
  };

  const handleShowDetail = async (record) => {
    setDetailVisible(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const response = await apiClient.get(`/api/admin/llm/users/${record.id}`);
      if (response.data?.success) {
        setDetail(response.data.data || null);
      } else {
        message.error(response.data?.message || '获取用户详情失败');
      }
    } catch (error) {
      message.error('获取用户详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleStatus = async (record) => {
    const nextStatus = record.status === 'active' ? 'suspended' : 'active';
    setStatusSubmittingId(record.id);
    try {
      const response = await apiClient.put(`/api/admin/llm/users/${record.id}/status`, {
        status: nextStatus,
      });
      if (response.data?.success) {
        message.success(response.data?.message || '状态更新成功');
        fetchUsers();
      } else {
        message.error(response.data?.message || '状态更新失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '状态更新失败');
    } finally {
      setStatusSubmittingId(null);
    }
  };

  const handleOpenQuota = (record) => {
    setQuotaTarget(record);
    quotaForm.setFieldsValue({ quota: record.quota ?? 0 });
    setQuotaVisible(true);
  };

  const handleSubmitQuota = async () => {
    try {
      const values = await quotaForm.validateFields();
      setQuotaSubmitting(true);
      const response = await apiClient.put(`/api/admin/llm/users/${quotaTarget.id}/quota`, {
        quota: values.quota,
      });
      if (response.data?.success) {
        message.success(response.data?.message || '配额更新成功');
        setQuotaVisible(false);
        setQuotaTarget(null);
        fetchUsers();
      } else {
        message.error(response.data?.message || '配额更新失败');
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.response?.data?.message || '配额更新失败');
    } finally {
      setQuotaSubmitting(false);
    }
  };

  const formatQuota = formatCompactNumber;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'yeap 用户 ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 120,
    },
    {
      title: 'LLM 用户 ID',
      dataIndex: 'llm_user_id',
      key: 'llm_user_id',
      width: 120,
    },
    {
      title: 'LLM 用户名',
      dataIndex: 'llm_username',
      key: 'llm_username',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <LlmStatusTag status={status} />,
    },
    {
      title: '配额',
      dataIndex: 'quota',
      key: 'quota',
      align: 'right',
      render: formatQuota,
    },
    {
      title: '已使用',
      dataIndex: 'used_quota',
      key: 'used_quota',
      align: 'right',
      render: formatQuota,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time) => time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleShowDetail(record)}>
            详情
          </Button>
          <Popconfirm
            title={record.status === 'active' ? '确定暂停该用户？' : '确定恢复该用户？'}
            onConfirm={() => handleToggleStatus(record)}
            okText="确定"
            cancelText="取消"
            disabled={record.status === 'deleted'}
          >
            <Button
              type="link"
              size="small"
              danger={record.status === 'active'}
              loading={statusSubmittingId === record.id}
              disabled={record.status === 'deleted'}
            >
              {record.status === 'active' ? '暂停' : '恢复'}
            </Button>
          </Popconfirm>
          <Button type="link" size="small" onClick={() => handleOpenQuota(record)}>
            改配额
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={<><RobotOutlined /> LLM 用户管理</>}
      extra={
        <Space>
          <Input
            placeholder="搜索用户名"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 200 }}
            allowClear
          />
          <Button onClick={handleSearch}>搜索</Button>
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={(newPagination) => {
          setPagination(prev => ({
            ...prev,
            current: newPagination.current,
          }));
        }}
      />

      <Modal
        title="用户详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={<Button onClick={() => setDetailVisible(false)}>关闭</Button>}
      >
        {detail ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{detail.id}</Descriptions.Item>
            <Descriptions.Item label="yeap 用户 ID">{detail.user_id}</Descriptions.Item>
            <Descriptions.Item label="yeap 用户名">{detail.yeap_user?.username || '-'}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{detail.yeap_user?.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="LLM 用户 ID">{detail.llm_user_id}</Descriptions.Item>
            <Descriptions.Item label="LLM 用户名">{detail.llm_username}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <LlmStatusTag status={detail.status} />
            </Descriptions.Item>
            <Descriptions.Item label="配额">{formatQuota(detail.quota)}</Descriptions.Item>
            <Descriptions.Item label="已使用">{formatQuota(detail.used_quota)}</Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {detail.created_at ? new Date(detail.created_at).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {detail.updated_at ? new Date(detail.updated_at).toLocaleString() : '-'}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: 24 }}>{detailLoading ? '加载中...' : '暂无数据'}</div>
        )}
      </Modal>

      <Modal
        title="调整配额"
        open={quotaVisible}
        onCancel={() => setQuotaVisible(false)}
        onOk={handleSubmitQuota}
        confirmLoading={quotaSubmitting}
        okText="保存"
        cancelText="取消"
      >
        <Form form={quotaForm} layout="vertical">
          <Form.Item
            name="quota"
            label="配额"
            rules={[{ required: true, message: '请输入配额值' }]}
          >
            <InputNumber
              min={0}
              max={100000000000}
              style={{ width: '100%' }}
              placeholder="请输入配额值"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default LlmUsers;
